import { useRef } from 'react'
import { Button } from '@ascleo/ui/components/ui/button'
import { cn } from '@ascleo/ui/lib/utils'
import {
  CalendarDays,
  CalendarOff,
  ChevronLeft,
  ChevronRight,
  Clock,
  House,
  Moon,
  RotateCw,
  Sparkles,
  Sun,
  Sunrise,
  Sunset,
  type LucideIcon
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { formatIsoDateForLocale, firstDayOfIsoMonth, isoWeekday } from '@ascleo/types/date-time-validity'
import { KioskStep } from '../../../../../components/kiosk-step'
import { KioskLoadingPanel, KioskStatePanel } from '../../../../../components/kiosk-state-panel'
import { useSchedulerExitToStart } from '../context'
import type {
  CalendarDay,
  QuickSlot,
  SelectableSlot,
  SlotWithTime,
  TimePeriod
} from './slot-selection-model'

const TIME_PERIOD_OPTIONS: ReadonlyArray<{
  key: TimePeriod
  icon: LucideIcon
}> = [
  { key: 'morning', icon: Sunrise },
  { key: 'midday', icon: Sun },
  { key: 'afternoon', icon: Sunset },
  { key: 'evening', icon: Moon }
]

function monthTitle(date: string, locale: string): string {
  return formatIsoDateForLocale(date, locale, { month: 'long', year: 'numeric' })
}

/**
 * Renders the scheduler slot loading state.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <SlotSelectionLoadingView />
 * ```
 */
export function SlotSelectionLoadingView() {
  const { t } = useTranslation()

  return (
    <KioskStep width="narrow" align="center" title={t('self-service.scheduler.slot-selection.title')}>
      <KioskLoadingPanel title={t('self-service.scheduler.slot-selection.loading')} />
    </KioskStep>
  )
}

/**
 * Renders the scheduler slot load error state with retry and a way home.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <SlotSelectionErrorView message="Failed" onRetry={() => undefined} />
 * ```
 */
export function SlotSelectionErrorView({
  message,
  onRetry,
}: {
  message: string
  onRetry: () => void
}) {
  const { t } = useTranslation()
  const exitToStart = useSchedulerExitToStart()

  return (
    <KioskStep width="narrow" align="center" title={t('self-service.scheduler.slot-selection.title')}>
      <KioskStatePanel
        tone="error"
        icon={<CalendarOff />}
        title={message}
        body={t('self-service.help-reception-toast')}
        action={{ label: t('common.retry'), icon: RotateCw, onClick: onRetry, testId: 'slot-selection-retry' }}
        secondaryAction={{
          label: t('self-service.scheduler.done.back-to-start'),
          icon: House,
          onClick: exitToStart,
          testId: 'slot-selection-home'
        }}
      />
    </KioskStep>
  )
}

/**
 * Renders the empty month instead of a greyed-out calendar (KO-64): plain
 * wording, the month named, and the month navigation as actions so the
 * patient can look further ahead.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <SlotSelectionEmptyView monthLabel="Oktober 2026" canGoPreviousMonth={false} onNextMonth={() => undefined} onPreviousMonth={() => undefined} />
 * ```
 */
export function SlotSelectionEmptyView({
  monthLabel,
  canGoPreviousMonth,
  onNextMonth,
  onPreviousMonth,
}: {
  monthLabel: string
  canGoPreviousMonth: boolean
  onNextMonth: () => void
  onPreviousMonth: () => void
}) {
  const { t } = useTranslation()

  return (
    <KioskStatePanel
      tone="neutral"
      testId="frontdesk-scheduler-slots-empty"
      icon={<CalendarOff />}
      title={t('self-service.scheduler.slot-selection.empty-title', { month: monthLabel })}
      body={t('self-service.scheduler.slot-selection.empty-body')}
      action={{
        label: t('self-service.scheduler.slot-selection.next-month'),
        icon: ChevronRight,
        onClick: onNextMonth,
        testId: 'frontdesk-scheduler-empty-next-month-btn'
      }}
      secondaryAction={
        canGoPreviousMonth
          ? {
              label: t('self-service.scheduler.slot-selection.previous-month'),
              icon: ChevronLeft,
              onClick: onPreviousMonth,
              testId: 'frontdesk-scheduler-empty-prev-month-btn'
            }
          : undefined
      }
    />
  )
}

/**
 * Renders the next available scheduler slot shortcuts (56px targets).
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <QuickSlotsStrip quickSlots={[]} onSelectSlot={() => undefined} />
 * ```
 */
export function QuickSlotsStrip({
  quickSlots,
  onSelectSlot,
}: {
  quickSlots: ReadonlyArray<QuickSlot>
  onSelectSlot: (slot: SelectableSlot) => void
}) {
  const { t } = useTranslation()

  if (quickSlots.length === 0) return null

  return (
    <section className="flex min-w-0 flex-col gap-3 rounded-3xl border border-brand-supporting/30 bg-brand-supporting-soft p-5">
      <h2 className="m-0 flex items-center gap-2 text-lg font-semibold text-brand-supporting-ink">
        <Sparkles aria-hidden className="size-6" />
        {t('self-service.scheduler.slot-selection.quick-select')}
      </h2>
      <div className="flex min-w-0 flex-wrap gap-3">
        {quickSlots.map((slot) => (
          <button
            key={slot.id}
            type="button"
            data-testid={`quick-slot-${slot.id}`}
            onClick={() => onSelectSlot(slot)}
            className="flex min-h-14 items-center gap-3 rounded-2xl bg-card px-4 py-3 text-left ring-2 ring-brand-supporting/40 transition-[transform,box-shadow] outline-none hover:ring-brand-supporting focus-visible:ring-4 focus-visible:ring-ring/45 motion-safe:active:scale-[0.98]"
          >
            <Clock aria-hidden className="size-6 shrink-0 text-brand-supporting-ink" />
            <span className="leading-tight">
              <span className="block text-base font-medium text-foreground">{slot.dateFormatted}</span>
              <span className="block text-2xl font-semibold">
                {t('self-service.scheduler.slot-selection.time-value', { time: slot.time })}
              </span>
            </span>
          </button>
        ))}
      </div>
    </section>
  )
}

function MonthCalendarPanel({
  viewMonth,
  dateLabelLocale,
  calendarDays,
  effectiveSelectedDate,
  canGoPreviousMonth,
  onPreviousMonth,
  onNextMonth,
  onSelectDay,
}: {
  viewMonth: string
  dateLabelLocale: string
  calendarDays: ReadonlyArray<CalendarDay>
  effectiveSelectedDate: string | null
  canGoPreviousMonth: boolean
  onPreviousMonth: () => void
  onNextMonth: () => void
  onSelectDay: (date: string) => void
}) {
  const { t } = useTranslation()
  const firstDayOffset = isoWeekday(firstDayOfIsoMonth(viewMonth)) - 1

  return (
    <section className="min-w-0 rounded-3xl border bg-card p-5">
      <h2 className="m-0 mb-3 flex items-center gap-2 text-lg font-semibold">
        <CalendarDays aria-hidden className="size-6 text-primary" />
        {t('self-service.scheduler.slot-selection.choose-day')}
      </h2>
      <div className="mb-4 flex items-center justify-between gap-2">
        <Button
          data-testid="frontdesk-scheduler-prev-month-btn"
          variant="outline"
          size="icon"
          aria-label={t('self-service.scheduler.slot-selection.previous-month')}
          onClick={onPreviousMonth}
          disabled={!canGoPreviousMonth}
          className="size-14 rounded-2xl"
        >
          <ChevronLeft aria-hidden className="size-7" />
        </Button>
        <p className="m-0 font-heading text-2xl font-normal" aria-live="polite">
          {monthTitle(viewMonth, dateLabelLocale)}
        </p>
        <Button
          data-testid="frontdesk-scheduler-next-month-btn"
          variant="outline"
          size="icon"
          aria-label={t('self-service.scheduler.slot-selection.next-month')}
          onClick={onNextMonth}
          className="size-14 rounded-2xl"
        >
          <ChevronRight aria-hidden className="size-7" />
        </Button>
      </div>

      <div className="mb-1.5 grid grid-cols-7 gap-1.5">
        {['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map((day) => (
          <div
            key={day}
            className="py-1 text-center text-base font-medium text-muted-foreground"
          >
            {t(`self-service.scheduler.slot-selection.weekday-${day}`)}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1.5">
        {Array.from({ length: firstDayOffset }, (_, index) => (
          <div key={`empty-${index}`} className="aspect-square" />
        ))}
        {calendarDays.map((day) => {
          const isSelected = effectiveSelectedDate === day.date
          const hasSlots = day.slotCount > 0
          const isDisabled = day.isPast || !hasSlots

          return (
            <button
              key={day.date}
              type="button"
              data-testid={`frontdesk-scheduler-day-${day.date}-btn`}
              aria-pressed={isSelected}
              onClick={() => !isDisabled && onSelectDay(day.date)}
              disabled={isDisabled}
              className={cn(
                'grid aspect-square min-h-12 place-items-center rounded-2xl text-lg font-semibold transition-colors outline-none focus-visible:ring-4 focus-visible:ring-ring/45',
                isSelected
                  ? 'bg-brand-supporting text-brand-supporting-foreground ring-2 ring-brand-supporting'
                  : hasSlots
                    ? 'bg-brand-supporting-subtle text-brand-supporting-ink hover:brightness-[0.97]'
                    : 'font-normal text-muted-foreground',
                day.isToday && !isSelected && 'ring-2 ring-brand-supporting/50'
              )}
            >
              {day.dayNum}
            </button>
          )
        })}
      </div>
    </section>
  )
}

function SlotTimesPanel({
  dayLabel,
  slotsByPeriod,
  onSelectSlot,
}: {
  dayLabel: string
  slotsByPeriod: Record<TimePeriod, SlotWithTime[]>
  onSelectSlot: (slot: SelectableSlot) => void
}) {
  const { t } = useTranslation()
  // Only periods with free times are shown: no "Keine Slots" columns and no
  // filter chips that repeat the columns (KO-61, KO-62).
  const periods = TIME_PERIOD_OPTIONS.filter(({ key }) => slotsByPeriod[key].length > 0)

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <h2 className="m-0 flex items-center gap-2 text-xl font-semibold">
        <Clock aria-hidden className="size-6 text-primary" />
        {dayLabel === ''
          ? t('self-service.scheduler.slot-selection.choose-day-first')
          : t('self-service.scheduler.slot-selection.times-heading', { date: dayLabel })}
      </h2>
      <div className="grid min-w-0 grid-cols-1 gap-5 @lg/slot-times:grid-cols-2">
        {periods.map(({ key, icon: Icon }) => (
          <section key={key} className="flex min-w-0 flex-col gap-2.5">
            <h3 className="m-0 flex items-center gap-2 text-base font-semibold text-muted-foreground">
              <Icon aria-hidden className="size-5" />
              {t(`self-service.scheduler.slot-selection.period-${key}`)}
            </h3>
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
              {slotsByPeriod[key].map((slot) => (
                <button
                  key={slot.id}
                  type="button"
                  data-testid={`time-slot-${slot.id}`}
                  onClick={() => onSelectSlot(slot)}
                  className="flex min-h-14 items-center justify-center gap-2 rounded-2xl border-2 bg-card px-3 py-3 text-xl font-semibold transition-colors outline-none hover:border-primary-vivid hover:bg-primary-subtle focus-visible:ring-4 focus-visible:ring-ring/45"
                >
                  <Clock aria-hidden className="size-5 shrink-0 text-muted-foreground" />
                  {slot.time}
                </button>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  )
}

/**
 * Renders the scheduler month calendar and the chosen day's free times. On
 * narrow layouts the times sit below the calendar, so choosing a day scrolls
 * them into view (KO-63).
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <SlotPickerPanel
 *   dayLabel=""
 *   viewMonth="2026-06-01"
 *   dateLabelLocale="de"
 *   calendarDays={[]}
 *   effectiveSelectedDate={null}
 *   canGoPreviousMonth={false}
 *   slotsByPeriod={{ morning: [], midday: [], afternoon: [], evening: [] }}
 *   onPreviousMonth={() => undefined}
 *   onNextMonth={() => undefined}
 *   onSelectDay={() => undefined}
 *   onSelectSlot={() => undefined}
 * />
 * ```
 */
export function SlotPickerPanel({
  dayLabel,
  viewMonth,
  dateLabelLocale,
  calendarDays,
  effectiveSelectedDate,
  canGoPreviousMonth,
  slotsByPeriod,
  onPreviousMonth,
  onNextMonth,
  onSelectDay,
  onSelectSlot,
}: {
  dayLabel: string
  viewMonth: string
  dateLabelLocale: string
  calendarDays: ReadonlyArray<CalendarDay>
  effectiveSelectedDate: string | null
  canGoPreviousMonth: boolean
  slotsByPeriod: Record<TimePeriod, SlotWithTime[]>
  onPreviousMonth: () => void
  onNextMonth: () => void
  onSelectDay: (date: string) => void
  onSelectSlot: (slot: SelectableSlot) => void
}) {
  const timesRef = useRef<HTMLDivElement | null>(null)

  const selectDayAndRevealTimes = (date: string) => {
    onSelectDay(date)
    const reduceMotion =
      typeof globalThis.matchMedia === 'function' &&
      globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches
    globalThis.requestAnimationFrame?.(() => {
      timesRef.current?.scrollIntoView?.({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'nearest' })
    })
  }

  return (
    <div className="@container/slot-picker min-w-0">
      <div className="grid min-w-0 grid-cols-1 items-start gap-6 @4xl/slot-picker:grid-cols-[minmax(0,26rem)_minmax(0,1fr)]">
        <MonthCalendarPanel
          viewMonth={viewMonth}
          dateLabelLocale={dateLabelLocale}
          calendarDays={calendarDays}
          effectiveSelectedDate={effectiveSelectedDate}
          canGoPreviousMonth={canGoPreviousMonth}
          onPreviousMonth={onPreviousMonth}
          onNextMonth={onNextMonth}
          onSelectDay={selectDayAndRevealTimes}
        />
        <div ref={timesRef} className="@container/slot-times min-w-0 scroll-mt-6">
          <SlotTimesPanel
            dayLabel={dayLabel}
            slotsByPeriod={slotsByPeriod}
            onSelectSlot={onSelectSlot}
          />
        </div>
      </div>
    </div>
  )
}
